/**
 * The signed-in person's own sign-in settings: authenticator-app two-factor and recovery codes.
 *
 *   GET  /v1/me/two-factor                    on or off, and recovery codes left
 *   POST /v1/me/two-factor                    a new secret and its otpauth:// link (nothing changes yet)
 *   POST /v1/me/two-factor/confirm            turn on with a code; returns recovery codes once
 *   POST /v1/me/two-factor/recovery-codes     replace recovery codes (needs a code)
 *   POST /v1/me/two-factor/disable            turn off (needs a code)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { withAccountConfirmation } from "../account-confirmation";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { Schemas } from "../types";

export type TwoFactorStatus = Schemas["TwoFactorStatus"];

export function useTwoFactor() {
  return useQuery({ queryKey: qk.twoFactor(), queryFn: () => unwrap(api.GET("/v1/me/two-factor")) });
}

export function useBeginTwoFactor() {
  return useMutation({ mutationFn: () => withAccountConfirmation("totp:enable", (headers) => unwrap(api.POST("/v1/me/two-factor", { headers, params: { header: idem() } }))) });
}

export function useConfirmTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.POST("/v1/me/two-factor/confirm", { params: { header: idem() }, body: { code } })),
    onSuccess: (res) => qc.setQueryData(qk.twoFactor(), res.status),
  });
}

export function useRegenerateRecoveryCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.POST("/v1/me/two-factor/recovery-codes", { params: { header: idem() }, body: { code } })),
    onSuccess: (res) => qc.setQueryData(qk.twoFactor(), res.status),
  });
}

export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => unwrap(api.POST("/v1/me/two-factor/disable", { params: { header: idem() }, body: { code } })),
    onSuccess: (res) => qc.setQueryData(qk.twoFactor(), res.status),
  });
}
